import React, { useEffect, useState, useRef } from "react";
import SidebarItem from "./SidebarItem";
import { FaCrown, FaTrash, FaEllipsisV } from "react-icons/fa";
import "../css/SidebarItem.css";
import api from "../api";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedMenu, setRefreshSidebar } from "../redux/sidebarSlice";
import { setSessionId } from "../redux/sessionSlice";
import { triggerRefresh } from "../redux/refreshSlice"; // ✅ Import refresh trigger
import { setSelectedModel, setSelectedMode } from "../redux/modelSlice";
import ChatIcon from "../assets/UserIcons/NewChatIconWhite.svg";
import TrainIcon from "../assets/UserIcons/LearningSpace.svg";
import HistoryIcon from "../assets/UserIcons/KnowledgeRepositoryIcon.svg";
import FolderNav from "../assets/UserIcons/MyKnowledgeSpace.svg";
import onlineIcon from "../assets/UserIcons/OnlineMode.svg";
import offlineIcon from "../assets/UserIcons/OfflineModeWhite.svg";
import crossIcon from "../assets/UserIcons/Cancel.svg";
import deleteIcon from "../assets/UserIcons/DeleteIcon.svg";
import { setPrivacyMode } from "../redux/privacySlice";
import VelaKoda from "../assets/Images/LoginLogo.svg";
import VelaLogo from "../assets/Images/ZeroBoxLogo.svg";
import { showErrorToast } from "../utils/toastUtils";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import { useBuildMeta } from "../version/useBuildMeta";
import { UpdateManager } from "../services/UpdateManager";
import ReleaseNotesModal from "./ReleaseNotesModal";

const Sidebar = ({ expanded, toggleSidebar }) => {
  const buildMeta = useBuildMeta();
  const currentAppVersion = buildMeta?.version
    ? `v${buildMeta.version}`
    : "v0.0.0";
  const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);
  const [releaseNotes, setReleaseNotes] = useState(() =>
    Array.isArray(UpdateManager.state?.releaseNotes)
      ? UpdateManager.state.releaseNotes
      : [],
  );
  const [sessions, setSessions] = useState([]);
  const [documentSessions, setDocumentSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSessionsToDelete, setSelectedSessionsToDelete] = useState([]);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [showMobileEdit, setShowMobileEdit] = useState(false);
  const [mobileSession, setMobileSession] = useState(null);
  const [openMenuSessionId, setOpenMenuSessionId] = useState(null);
  const dropdownRef = useRef(null);
  const chatHistoryRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const dispatch = useDispatch();
  const refreshSidebar = useSelector((state) => state.sidebar.refreshSidebar);
  const selectedMenu = useSelector((state) => state.sidebar.selectedMenu);
  const isFullPrivacy = useSelector((state) => state.privacy.isFullPrivacy);
  // console.log("Full Privacy Mode:", isFullPrivacy);

  const [collapsedSections, setCollapsedSections] = useState({
    today: false,
    yesterday: false,
    older: true, // 👈 collapsed by default
  });

  const toggleSection = (section) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleMenuClick = (label) => {
    dispatch(setSelectedMenu(label));
  };

  const isMobile = window.matchMedia("(max-width: 768px)").matches;

  useEffect(() => {
    const unsub = UpdateManager.subscribe((s) => {
      setReleaseNotes(Array.isArray(s?.releaseNotes) ? s.releaseNotes : []);
    });
    return () => unsub?.();
  }, []);

  useEffect(() => {
    // Set default selected menu based on URL path
    const pageLabelsMap = {
      "/user/new": "New Chat",
      "/user/notifications": "Notifications",
      "/user/profile": "Profile Settings",
      "/user/getHelpPage": "Get Help",
      "/user/training-studio":
        "Training Studio - Upload training materials, tutorials, URLs, videos, and audio to teach and improve your bot.",
      "/user/knowledge-repository":
        "Knowledge Repository - Centralized view and manage all training materials",
      "/user/knowledgerepository-weburls":
        "Knowledge Repository - Centralized view and manage all training materials",
      "/user/knowledgerepository-videourls":
        "Knowledge Repository - Centralized view and manage all training materials",
      "/user/knowledgerepository-deletedocs":
        "Knowledge Repository - Centralized view and manage all training materials",
      "/user/texthistory":
        "Knowledge Repository - Centralized view and manage all training materials",
      "/user/qahistory":
        "Knowledge Repository - Centralized view and manage all training materials",
      "/user/personal-space":
        "My Personal Space - A secure place to save, access, and send data for knowledge repository training anytime.",
      "/user/knowledgespace-deleted":
        "My Personal Space - A secure place to save, access, and send data for knowledge repository training anytime.",
      "/user/premium-page":
        "Premium - Try new experiences with Premium features.",
      "/user/repo-organizer": "Repository Organizer - Manage, move, and organize repository files efficiently.",
    };

    const currentPath = pageLabelsMap[location.pathname] || "";

    // update redux only if changed
    if (selectedMenu !== currentPath) {
      dispatch(setSelectedMenu(currentPath));
    }
  }, [location.pathname, selectedMenu, dispatch]);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const fetchChatHistory = async () => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/chat/sessions`,
      );

      let sessions = res.data.sessions || [];

      // ❗ Optional: keep only non-incognito
      // sessions = sessions.filter((session) => session.mode !== "incognito");

      // Set full list
      setSessions(sessions);

      // console.log("All Sessions Loaded:", sessions);
    } catch (err) {
      console.error("Failed to load chat sessions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchChatHistory();
    }
  }, [token, isFullPrivacy]);

  // useEffect(() => {
  //   // ✅ Redirect to New Chat when switching to Full Privacy
  //   if (isFullPrivacy) {
  //     // Clear any session state
  //     dispatch(setSessionId(null));

  //     // Navigate to New Chat page
  //     navigate("/user/new", { replace: true, state: { sessionId: null } });
  //   }
  // }, [isFullPrivacy, dispatch, navigate]);

  useEffect(() => {
    if (refreshSidebar && token) {
      fetchChatHistory();
      dispatch(setRefreshSidebar(false));
    }
  }, [refreshSidebar, token, isFullPrivacy]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Close dropdown if clicked outside
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }

      // Reset selection if clicked outside chat history area
      if (
        chatHistoryRef.current &&
        !chatHistoryRef.current.contains(e.target)
      ) {
        setSelectMode(false);
        setSelectedSessions([]);
      }

      // ✅ Mobile-only sidebar close
      if (
        window.innerWidth < 768 && // 👈 Mobile screen check
        expanded &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target)
      ) {
        toggleSidebar();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [expanded, toggleSidebar]);

  useEffect(() => {
    const stateSessionId = location.state?.sessionId || null;
    setActiveSessionId(stateSessionId);
  }, [location.state?.sessionId]);

  const toggleSelect = (type) => {
    setSelectMode(true);
    setDropdownOpen(false);

    // Match the same filter used for rendering chat history items
    const selectableSessionIds = sessions
      .filter(
        (session) =>
          session.first_input && session.first_input.trim().length > 0,
      )
      .map((s) => s.session_id);

    if (type === "all") {
      setSelectedSessions((prev) => {
        const allSelected =
          selectableSessionIds.length > 0 &&
          selectableSessionIds.every((id) => prev.includes(id));
        return allSelected ? [] : selectableSessionIds;
      });
      return;
    }

    // "Select Items" just enables selection mode with no preselection
    setSelectedSessions([]);
  };

  const handleEditClick = (e, session) => {
    e?.stopPropagation(); // 🚫 prevent navigation
    setEditingSessionId(session.session_id);
    setEditedName(session.session_name || session.first_input || "");
  };

  const handleEditCancel = () => {
    setEditingSessionId(null);
    setEditedName("");
  };

  const handleEditClickformobile = (e, session) => {
    e?.stopPropagation();

    if (isMobile) {
      setMobileSession(session);
      setEditedName(session.session_name || "");
      setShowMobileEdit(true);
      return;
    }

    // existing desktop behavior
    setEditingSessionId(session.session_id);
    setEditedName(session.session_name || "");
  };

  const handleEditSave = async (sessionId) => {
    const trimmedName = editedName.trim();

    if (!trimmedName) {
      // ❌ Empty input → cancel edit and restore previous value
      handleEditCancel();
      return;
    }

    try {
      await api.put(`${process.env.REACT_APP_API_URL}/chat/sessions/update`, {
        session_id: sessionId,
        session_name: editedName.trim(),
      });

      // ✅ Optimistic UI update (no UI flicker)
      setSessions((prev) =>
        prev.map((s) =>
          s.session_id === sessionId
            ? { ...s, session_name: editedName.trim() }
            : s,
        ),
      );
    } catch (error) {
      console.error("❌ Failed to update session name:", error);
    } finally {
      setEditingSessionId(null);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedSessions.length === 0) {
      showErrorToast("Please select at least one chat to delete.");
      return;
    }
    setSelectedSessionsToDelete(selectedSessions);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const normalIds = selectedSessionsToDelete.filter((id) =>
        sessions.some((s) => s.session_id === id),
      );

      // Delete normal sessions individually
      await Promise.all(
        normalIds.map((id) =>
          api.delete(`${process.env.REACT_APP_API_URL}/chat/history/${id}`),
        ),
      );

      // UI cleanup
      setSessions((prev) =>
        prev.filter((s) => !selectedSessionsToDelete.includes(s.session_id)),
      );
      setSelectedSessions([]);
      setSelectMode(false);
      setSelectedSessionsToDelete([]);
      await fetchChatHistory();
      dispatch(setSessionId(null));
      navigate("/user/new", { state: { sessionId: null } });
    } catch (err) {
      console.error("Failed to delete selected sessions:", err);
      showErrorToast("Failed to delete selected chats. Please try again.");
    }
  };

  const handleCheckboxChange = (sessionId) => {
    setSelectedSessions((prev) =>
      prev.includes(sessionId)
        ? prev.filter((id) => id !== sessionId)
        : [...prev, sessionId],
    );
  };

  const handleLogoClick = () => {
    navigate("/"); // navigate to login page
  };

  // useEffect(() => {
  //   const closeMenu = () => setOpenMenuSessionId(null);
  //   document.addEventListener("click", closeMenu);
  //   return () => document.removeEventListener("click", closeMenu);
  // }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      // if click is NOT inside the menu wrapper → close
      if (!e.target.closest(".session-menu-wrapper")) {
        setOpenMenuSessionId(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  return (
    <div
      ref={sidebarRef}
      className={`sidebar d-flex flex-column overflow-hiddenn  ${
        expanded ? "expanded" : "collapsed"
      }`}
    >
      <div>
        <div
          className={`logo-section ${
            expanded
              ? "d-flex justify-content-between align-items-center pe-3 py-2"
              : "d-flex align-items-center justify-content-center gap-2 py-2"
          }`}
        >
          {expanded ? (
            <>
              <img
                src={VelaKoda}
                alt="Logo"
                className="logo"
                style={{
                  width: "160px",
                  height: "30px",
                  objectFit: "contain",
                  cursor: "pointer",
                }}
                onClick={handleLogoClick}
              />
              <button onClick={toggleSidebar} className="sidebar-toggler ms-2">
                <i className="bi bi-chevron-double-left" />
              </button>
            </>
          ) : (
            <>
              <img
                src={VelaLogo}
                alt="Bot Icon"
                className={`bot-icon w-50 ${
                  expanded ? "expanded" : "collapsed"
                }`}
                style={{ cursoer: "pointer" }}
                onClick={handleLogoClick}
              />
              <button
                onClick={toggleSidebar}
                className={`sidebar-toggler ${
                  expanded ? "expanded" : "collapsed"
                }`}
              >
                <i className="bi bi-chevron-double-right" />
              </button>
            </>
          )}
        </div>
        <div>
          <ul className="menu m-0 ">
            <SidebarItem
              icon={ChatIcon}
              label="New Chat"
              to="/user/new"
              expanded={expanded}
              // onClick={() => setActiveSessionId(null)}
              // active={!activeSessionId}
              onClick={() => {
                handleMenuClick("New Chat");
                setActiveSessionId(null); // optional
                dispatch(setSessionId(null));
                // navigate(0);
              }}
              active={selectedMenu === "New Chat" && !activeSessionId}
            />
            {/* <SidebarItem
              icon={TrainIcon}
              label="Training Studio"
              to="/user/training-studio"
              expanded={expanded}
              onClick={() => {
                handleMenuClick("Training Studio");
                setActiveSessionId(null);
              }}                                                                                                                                                                                                                                                                                                                                                                          
              active={
                selectedMenu ===
                  "Training Studio - Upload training materials, tutorials, URLs, videos, and audio to teach and improve your bot." &&
                !activeSessionId
              }
            /> */}

            {/* <SidebarItem
              icon={HistoryIcon}
              label="Knowledge Repository"
              to="/user/knowledge-repository"
              expanded={expanded}
              onClick={() => {
                handleMenuClick("Knowledge Repository");
                setActiveSessionId(null);
              }}
              active={
                selectedMenu ===
                  "Knowledge Repository - Centralized view and manage all training materials" &&
                !activeSessionId
              }
            />

            <SidebarItem
              icon={FolderNav}
              label="My Personal Space"
              to="/user/personal-space"
              expanded={expanded}
              onClick={() => {
                handleMenuClick("My Personal Space");
                setActiveSessionId(null);
              }}
              active={
                selectedMenu ===
                  "My Personal Space - A secure place to save, access, and send data for knowledge repository training anytime." &&
                !activeSessionId
              }
            /> */}
            <SidebarItem
              icon={FolderNav}
              label="Repository Organizer"
              to="/user/repo-organizer"
              expanded={expanded}
              onClick={() => {
                handleMenuClick("Repository Organizer");
                setActiveSessionId(null);
              }}
              active={
                selectedMenu ===
                "Repository Organizer - Manage, move, and organize repository files efficiently."
              }
            />
          </ul>
        </div>
      </div>

      <div className="d-flex flex-column flex-grow-1 mt-3">
        <div
          className="d-flex flex-column flex-grow-1 overflow-hidden"
          style={{ minHeight: 0 }}
        >
          {/* Chat History */}
          {expanded && (
            <div
              className="m-1 d-flex flex-column flex-grow-1"
              style={{ minHeight: 0 }}
              ref={chatHistoryRef}
            >
              <div className="chat-history-title d-flex justify-content-between align-items-center">
                <p className="m-0">Chat History</p>
                <div
                  className="d-flex align-items-center position-relative"
                  ref={dropdownRef}
                >
                  {selectMode && (
                    <FaTrash
                      className="me-2"
                      style={{ cursor: "pointer" }}
                      onClick={handleDeleteSelected}
                      title="Delete selected"
                    />
                  )}
                  <FaEllipsisV
                    style={{ cursor: "pointer" }}
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  />
                  {dropdownOpen && (
                    <div
                      className="dropdown-menu-session"
                      style={{ top: "25px", right: 0 }}
                    >
                      <div onClick={() => toggleSelect("all")}>Select All</div>
                      <div onClick={() => toggleSelect("custom")}>
                        Select Items
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="chat-history-list overflow-y-scroll">
                {loading ? (
                  <div className="textwhite">Loading...</div>
                ) : (
                  (() => {
                    // 🔹 helpers (kept inside render to avoid refactor)
                    const isToday = (date) => {
                      const d = new Date(date);
                      const today = new Date();
                      return d.toDateString() === today.toDateString();
                    };

                    const isYesterday = (date) => {
                      const d = new Date(date);
                      const yesterday = new Date();
                      yesterday.setDate(yesterday.getDate() - 1);
                      return d.toDateString() === yesterday.toDateString();
                    };

                    let todayShown = false;
                    let yesterdayShown = false;
                    let olderShown = false;

                    const sortedSessions = [...sessions]
                      .filter(
                        (session) =>
                          session.first_input &&
                          session.first_input.trim().length > 0,
                      )
                      .sort(
                        (a, b) =>
                          new Date(b.latest_at || b.created_at) -
                          new Date(a.latest_at || a.created_at),
                      );

                    return sortedSessions.map((session) => {
                      const sessionDate =
                        session.latest_at || session.created_at;

                      const isCollapsed =
                        (isToday(sessionDate) && collapsedSections.today) ||
                        (isYesterday(sessionDate) &&
                          collapsedSections.yesterday) ||
                        (!isToday(sessionDate) &&
                          !isYesterday(sessionDate) &&
                          collapsedSections.older);

                      let heading = null;

                      if (isToday(sessionDate) && !todayShown) {
                        todayShown = true;
                        heading = (
                          <div
                            className="chat-section-header ak-text-lg"
                            onClick={() => toggleSection("today")}
                            style={{ cursor: "pointer" }}
                          >
                            Today {collapsedSections.today ? "▸" : "▾"}
                          </div>
                        );
                      } else if (isYesterday(sessionDate) && !yesterdayShown) {
                        yesterdayShown = true;
                        heading = (
                          <div
                            className="chat-section-header ak-text-lg"
                            onClick={() => toggleSection("yesterday")}
                            style={{ cursor: "pointer" }}
                          >
                            Yesterday {collapsedSections.yesterday ? "▸" : "▾"}
                          </div>
                        );
                      } else if (
                        !olderShown &&
                        !isToday(sessionDate) &&
                        !isYesterday(sessionDate)
                      ) {
                        olderShown = true;
                        heading = (
                          <div
                            className="chat-section-header ak-text-lg"
                            onClick={() => toggleSection("older")}
                            style={{ cursor: "pointer" }}
                          >
                            Older {collapsedSections.older ? "▸" : "▾"}
                          </div>
                        );
                      }

                      return (
                        <React.Fragment key={session.session_id}>
                          {heading}
                          {!isCollapsed && (
                            <div
                              className={`chat-history-item custom-hover d-flex align-items-center mb-1 px-2 py-1 ${
                                activeSessionId === session.session_id &&
                                expanded
                                  ? "active"
                                  : ""
                              }`}
                              style={{ overflow: "visible" }}
                            >
                              {/* CLICKABLE ROW */}
                              <div
                                className="d-flex align-items-center ak-text-md"
                                style={{ width: "90%" }}
                                onClick={() => {
                                  if (editingSessionId) return;
                                  if (selectMode) {
                                    handleCheckboxChange(session.session_id);
                                    return;
                                  }

                                  if (
                                    session.session_type?.toLowerCase() ===
                                    "online"
                                  ) {
                                    dispatch(setPrivacyMode(false));
                                  } else {
                                    dispatch(setPrivacyMode(true));
                                  }

                                  navigate("/user/new", {
                                    state: {
                                      sessionId: session.session_id,
                                      scrollToBottom: true,
                                    },
                                  });

                                  dispatch(setSessionId(session.session_id));
                                  dispatch(triggerRefresh());
                                }}
                              >
                                {selectMode && (
                                  <input
                                    type="checkbox"
                                    className="me-2 sidebar-check"
                                    checked={selectedSessions.includes(
                                      session.session_id,
                                    )}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleCheckboxChange(session.session_id);
                                    }}
                                  />
                                )}

                                <img
                                  src={
                                    session.session_type?.toLowerCase() ===
                                    "online"
                                      ? onlineIcon
                                      : offlineIcon
                                  }
                                  alt="mode-icon"
                                  className={
                                    session.session_type?.toLowerCase() ===
                                    "online"
                                      ? "me-2 chat_icon1"
                                      : "me-2 chat_icon chat_history_img"
                                  }
                                />

                                {/* LEFT: TITLE */}
                                <div
                                  className="flex-grow-1"
                                  title={editedName}
                                  style={{
                                    minWidth: 0,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {editingSessionId === session.session_id ? (
                                    <input
                                      id={`session-input-${session.session_id}`}
                                      type="text"
                                      value={
                                        editedName.charAt(0).toUpperCase() +
                                        editedName.slice(1)
                                      }
                                      autoFocus
                                      onChange={(e) =>
                                        setEditedName(e.target.value)
                                      }
                                      onBlur={() =>
                                        handleEditSave(session.session_id)
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter")
                                          handleEditSave(session.session_id);
                                        if (e.key === "Escape")
                                          handleEditCancel();
                                      }}
                                      style={{
                                        background: "transparent",
                                        border: "none",
                                        outline: "none",
                                        color: "inherit",
                                        width: "100%",
                                        fontSize: "inherit",
                                        fontFamily: "inherit",
                                      }}
                                    />
                                  ) : (
                                    (
                                      session.session_name ||
                                      session.first_input?.slice(0, 30) ||
                                      "New Chat"
                                    )?.replace(/^./, (c) => c.toUpperCase())
                                  )}
                                </div>
                              </div>

                              {/* RIGHT: THREE DOTS */}
                              <div
                                className="session-menu-wrapper"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                <i
                                  className="bi bi-three-dots-vertical edit-icon"
                                  onClick={() =>
                                    setOpenMenuSessionId(
                                      openMenuSessionId === session.session_id
                                        ? null
                                        : session.session_id,
                                    )
                                  }
                                />

                                {openMenuSessionId === session.session_id && (
                                  <div className="session-menu">
                                    <div
                                      className="menu-item"
                                      onClick={() => {
                                        setOpenMenuSessionId(null);
                                        window.innerWidth <= 768
                                          ? handleEditClickformobile(
                                              null,
                                              session,
                                            )
                                          : handleEditClick(null, session);
                                      }}
                                    >
                                      Rename
                                    </div>

                                    <div
                                      className="menu-item delete"
                                      onClick={() => {
                                        setOpenMenuSessionId(null);
                                        setSelectedSessionsToDelete([
                                          session.session_id,
                                        ]);
                                        setShowDeleteModal(true);
                                      }}
                                    >
                                      Delete
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    });
                  })()
                )}
              </div>
            </div>
          )}
          {showMobileEdit && (
            <div className="mobile-edit-overlay">
              <div className="mobile-edit-box">
                <h6>Edit Chat Name</h6>

                <input
                  type="text"
                  value={editedName}
                  autoFocus
                  onChange={(e) => setEditedName(e.target.value)}
                  placeholder="Enter chat name"
                />

                <div className="d-flex justify-content-end mt-3">
                  <button
                    className="cancle-btn me-2"
                    onClick={() => setShowMobileEdit(false)}
                  >
                    Cancel
                  </button>

                  <button
                    className="custom-btn"
                    onClick={() => {
                      handleEditSave(mobileSession.session_id);
                      setShowMobileEdit(false);
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          {showDeleteModal && selectedSessionsToDelete.length > 0 && (
            <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
              <div className="modal-history modal-dialog-centered history-model">
                <div className="modal-content">
                  {/* Close Button */}
                  <button
                    type="button"
                    className="modal-close-btn position-absolute top-0 end-0 mt-2 me-2"
                    onClick={() => setShowDeleteModal(false)}
                    aria-label="Close"
                  >
                    <img
                      src={crossIcon}
                      className="icon-image cross-i"
                      alt="Close"
                    />
                  </button>

                  {/* Modal Body */}
                  <div className="text-center p-3">
                    <div
                      className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "50%",
                        backgroundColor: "#f0f0f0",
                        // boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <img
                        src={deleteIcon} // ← your train icon import
                        alt="Training"
                        style={{ width: "40px", height: "40px" }}
                      />
                    </div>
                    <p className="mt-3 mb-3 refresh-prompts">
                      Are you sure you want to delete the selected chats? This
                      action cannot be undone.
                    </p>
                  </div>

                  {/* Modal Footer */}
                  <div className="modal-footer justify-content-center gap-2">
                    <button
                      type="button"
                      className="px-5 rounded cancle-btn border-none py-2 fw-bold"
                      onClick={() => setShowDeleteModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="px-5 rounded custom-btn border-none py-2 fw-bold history-btn"
                      onClick={async () => {
                        await handleConfirmDelete();
                        setShowDeleteModal(false);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Premium Section */}
        {/* <div className="p-2 mt-auto text-center">
          <div className={`premium-transition ${expanded ? "show" : "hide"}`}>
            {expanded ? (
              <div className="premium-card-container">
                <div className="premium-card-header">
                  <div className="premium-icon-box">
                    <FaCrown className="" size={18} />
                  </div>
                  <div>
                    <div className="premium-title refresh-prompts">
                      Get Premium
                    </div>
                    <div className="premium-description">
                      Try new experiences with premium features
                    </div>
                  </div>
                </div>
                <button
                  className="premium-upgrade-btn"
                  onClick={() => navigate("/premium-page")}
                >
                  Upgrade Plan
                </button>
                <span
                  className="text-center refresh-prompts"
                  style={{ fontSize: "small" }}
                >
                  v1.0.0
                </span>
              </div>
            ) : (
              <div
                className="d-flex justify-content-center"
                // onClick={() => navigate("/premium-page")} // 👈 logic written inline here
                style={{ cursor: "pointer" }}
              >
                <span className="text-center" style={{ fontSize: "small" }}>
                  v1.0.0
                </span>
              </div>
            )}
          </div>
        </div> */}
        {expanded && (
          <div className="text-center" style={{ color: "white" }}>
            <button
              type="button"
              className="btn btn-link p-0 m-0"
              style={{ color: "white", textDecoration: "none" }}
              onClick={() => setReleaseNotesOpen(true)}
              title="Release Notes"
            >
              {currentAppVersion}
            </button>
          </div>
        )}
      </div>

      <ReleaseNotesModal
        open={releaseNotesOpen}
        onClose={() => setReleaseNotesOpen(false)}
        notes={releaseNotes}
      />
    </div>
  );
};

export default Sidebar;
